const INIT_FLAG = 'stgbSsInit';

// Mirrors the config JSON emitted by save.php (script.stgb-ss-config). All of
// these values originate from editor input, so every string that lands in the
// DOM goes through textContent — never innerHTML.
interface SsOption {
    value: string;
    label: string;
    description?: string;
    isWildcard?: boolean;
    /** Product this answer implies (e.g. the chosen mount) — shown with the results. */
    productId?: number;
}

interface SsQuestion {
    key: string;
    prompt: string;
    helpText?: string;
    skipIfIrrelevant?: boolean;
    groupWhenWildcard?: boolean;
    options: SsOption[];
}

interface SsResult {
    productId: number;
    note?: string;
    badges?: string[];
}

interface SsRule {
    conditions: Record<string, string>;
    results: SsResult[];
}

interface SsConfig {
    questions: SsQuestion[];
    rules: SsRule[];
    labels: Record<string, string>;
    showProgress?: boolean;
}

const initSelector = (root: HTMLElement) => {
    if (root.dataset[INIT_FLAG] === 'true') return;

    const configEl = root.querySelector<HTMLScriptElement>('script.stgb-ss-config');
    const form = root.querySelector<HTMLFormElement>('[data-stgb-ss-steps]');
    const progressEl = root.querySelector<HTMLElement>('[data-stgb-ss-progress]');
    const backBtn = root.querySelector<HTMLButtonElement>('[data-stgb-ss-back]');
    const resultsEl = root.querySelector<HTMLElement>('[data-stgb-ss-results]');
    // The start screen (heading + intro + Start CTA) — hidden once the quiz
    // begins, shown again on restart.
    const headerEl = root.querySelector<HTMLElement>('[data-stgb-ss-header]');
    const startBtn = root.querySelector<HTMLButtonElement>('[data-stgb-ss-start]');

    if (!configEl || !form || !resultsEl) return;

    let config: SsConfig | null = null;
    try {
        config = JSON.parse(configEl.textContent || '');
    } catch (err) {
        config = null;
    }
    if (!config || !Array.isArray(config.questions) || !Array.isArray(config.rules)) return;

    root.dataset[INIT_FLAG] = 'true';

    const questions = config.questions;
    const rules = config.rules;
    const labels = config.labels || {};
    const showProgress = config.showProgress !== false;

    // Lookups built once: question by key, its rendered fieldset, and one
    // <template> per unique product (save.php renders each card exactly once,
    // even when several rules reference the same product).
    const questionByKey: Record<string, SsQuestion> = {};
    questions.forEach((q) => {
        questionByKey[q.key] = q;
    });

    const stepByKey: Record<string, HTMLFieldSetElement> = {};
    form.querySelectorAll<HTMLFieldSetElement>('.stgb-ss-step').forEach((step) => {
        if (step.dataset.question) stepByKey[step.dataset.question] = step;
    });

    const templateById: Record<string, HTMLTemplateElement> = {};
    root.querySelectorAll<HTMLTemplateElement>('template.stgb-ss-product-tpl').forEach((tpl) => {
        if (tpl.dataset.productId) templateById[tpl.dataset.productId] = tpl;
    });

    // question key -> chosen option value; trail = keys of questions actually
    // shown AND answered, in order, so Back can retrace skipped questions.
    const answers: Record<string, string> = {};
    const trail: string[] = [];
    let advanceTimer: number | undefined;

    // Add-all runs are fenced per selector instance: leaving the results
    // screen (Back / Start Over / any results re-render) bumps the generation
    // and aborts the in-flight request, so a re-entered results screen can
    // never run two interleaved sequential add loops double-adding products.
    let addAllGen = 0;
    let addAllAbort: AbortController | null = null;
    const invalidateAddAll = () => {
        addAllGen += 1;
        if (addAllAbort) {
            addAllAbort.abort();
            addAllAbort = null;
        }
    };

    /* ---- Pure quiz logic ---- */

    const optionFor = (q: SsQuestion | undefined, value: string): SsOption | undefined =>
        q ? q.options.find((o) => o.value === value) : undefined;

    // A "no preference" pick filters nothing.
    const isWildcardValue = (key: string, value: string): boolean => {
        const opt = optionFor(questionByKey[key], value);
        return !!(opt && opt.isWildcard);
    };

    // Rules that survive the given answers: every answered, non-wildcarded
    // question a rule conditions on must match exactly. Absent condition keys
    // match anything. Takes the answer map as a parameter because grouped
    // results re-run this with hypothetical answers.
    const candidatesFor = (ans: Record<string, string>): SsRule[] =>
        rules.filter((rule) =>
            Object.keys(rule.conditions).every((key) => {
                if (!(key in ans) || isWildcardValue(key, ans[key])) return true;
                return rule.conditions[key] === ans[key];
            })
        );

    const isRelevant = (q: SsQuestion, cands: SsRule[]): boolean =>
        cands.some((rule) => q.key in rule.conditions);

    // Wildcards always show; a concrete option shows when some candidate
    // matches it or some candidate doesn't condition on this question at all.
    // The FIRST question filters like every other one: an option no rule
    // references (e.g. a seeded caliber with no combinations yet) never
    // renders anywhere.
    const visibleOptions = (q: SsQuestion, cands: SsRule[]): SsOption[] => {
        const anyUnconditioned = cands.some((rule) => !(q.key in rule.conditions));
        return q.options.filter(
            (o) => !!o.isWildcard || anyUnconditioned || cands.some((rule) => rule.conditions[q.key] === o.value)
        );
    };

    // A skippable question hides when no candidate depends on it, or when the
    // candidates leave zero concrete options (orphaned condition values). One
    // visible option still shows — the client may want the confirmation step.
    // Non-skippable questions still need at least one visible option, or the
    // step would render empty with no way to advance.
    const wouldShow = (q: SsQuestion, cands: SsRule[]): boolean => {
        if (!q.skipIfIrrelevant) return visibleOptions(q, cands).length > 0;
        if (!isRelevant(q, cands)) return false;
        return visibleOptions(q, cands).some((o) => !o.isWildcard);
    };

    const nextQuestion = (): SsQuestion | null => {
        const cands = candidatesFor(answers);
        // No rule can match — go straight to results, which renders the
        // fallback panel, instead of asking questions that lead nowhere.
        if (!cands.length) return null;
        for (const q of questions) {
            if (q.key in answers) continue;
            if (!stepByKey[q.key]) continue; // config/markup out of sync — unanswerable
            if (wouldShow(q, cands)) return q;
        }
        return null;
    };

    // Specificity = condition keys exactly matched by a concrete (non-wildcard)
    // answer. Ties break toward document order (strict > keeps the first max).
    const bestRule = (cands: SsRule[], ans: Record<string, string>): SsRule | null => {
        let best: SsRule | null = null;
        let bestScore = -1;
        cands.forEach((rule) => {
            let score = 0;
            Object.keys(rule.conditions).forEach((key) => {
                if (key in ans && !isWildcardValue(key, ans[key]) && rule.conditions[key] === ans[key]) {
                    score += 1;
                }
            });
            if (score > bestScore) {
                best = rule;
                bestScore = score;
            }
        });
        return best;
    };

    /* ---- Result DOM builders ---- */

    const make = (tag: string, className: string, text?: string): HTMLElement => {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text !== undefined) node.textContent = text;
        return node;
    };

    // Clone the server-rendered card for one result and stamp its per-rule
    // extras (badge chips, note). Null when the product has no template (e.g.
    // the product was unpublished after this page was cached).
    const buildCard = (result: SsResult): HTMLElement | null => {
        const tpl = templateById[String(result.productId)];
        if (!tpl) return null;

        const card = make('div', 'stgb-ss-card');
        card.appendChild(tpl.content.cloneNode(true));

        const body = card.querySelector<HTMLElement>('.stgb-pc-body');
        if (body) {
            const badges = result.badges || [];
            if (badges.length) {
                const chips = make('div', 'stgb-ss-chips');
                badges.forEach((badge) => {
                    chips.appendChild(make('span', 'stgb-ss-chip', badge));
                });
                body.insertBefore(chips, body.firstChild);
            }
            if (result.note) {
                // Before the CTA button; insertBefore(node, null) appends.
                body.insertBefore(
                    make('p', 'stgb-ss-note', result.note),
                    body.querySelector('.stgb-pc-button')
                );
            }
        }
        return card;
    };

    // The featured card reuses product-collection's media badge so it matches
    // the shared card styling exactly.
    const stampFeatured = (card: HTMLElement) => {
        const media = card.querySelector<HTMLElement>('.stgb-pc-media');
        if (!media || !labels.featuredLabel) return;
        const badge = make('span', 'stgb-pc-badge');
        badge.appendChild(make('span', 'stgb-pc-badge-text', labels.featuredLabel));
        media.appendChild(badge);
    };

    // Result cards for one rule. Results with missing templates are skipped;
    // the first renderable one is the best match and carries the featured
    // badge. A lone match renders as the large centred featured card; several
    // matches render as ONE row of equal peers (stacked on mobile) — no
    // separate alternates section or heading. Rendered product ids land in
    // shownIds so the answer add-ons can dedupe.
    const buildPanel = (rule: SsRule, shownIds: Set<number>): HTMLElement | null => {
        const cards: HTMLElement[] = [];
        rule.results.forEach((result) => {
            const card = buildCard(result);
            if (card) {
                cards.push(card);
                shownIds.add(result.productId);
            }
        });
        if (!cards.length) return null;

        stampFeatured(cards[0]);

        const panel = make('div', 'stgb-ss-result-panel');
        if (cards.length === 1) {
            const featured = make('div', 'stgb-ss-featured');
            featured.appendChild(cards[0]);
            panel.appendChild(featured);
        } else {
            const grid = make('div', 'stgb-ss-cards stgb-ss-matches');
            cards.forEach((card) => grid.appendChild(card));
            panel.appendChild(grid);
        }
        return panel;
    };

    // Cards for products the visitor's answers imply (options with a linked
    // product — e.g. their chosen mount): the config carries the option's
    // productId and save.php shipped a <template> for it. Deduped against
    // every card already shown — if the matched rule already recommends the
    // product, one card is enough. Wildcard answers imply nothing.
    const buildAnswerAddons = (shownIds: Set<number>): HTMLElement | null => {
        const cards: HTMLElement[] = [];
        trail.forEach((key) => {
            const q = questionByKey[key];
            const opt = optionFor(q, answers[key]);
            if (!opt || !opt.productId || opt.isWildcard) return;
            if (shownIds.has(opt.productId)) return;
            const tpl = templateById[String(opt.productId)];
            if (!tpl) return;

            const card = make('div', 'stgb-ss-card stgb-ss-answer-card');
            card.appendChild(tpl.content.cloneNode(true));
            // Stamp which answer brought this card in, chip-style.
            const body = card.querySelector<HTMLElement>('.stgb-pc-body');
            if (body) {
                const chips = make('div', 'stgb-ss-chips');
                chips.appendChild(make('span', 'stgb-ss-chip', opt.label));
                body.insertBefore(chips, body.firstChild);
            }
            shownIds.add(opt.productId);
            cards.push(card);
        });
        if (!cards.length) return null;

        const section = make('section', 'stgb-ss-answer-addons');
        section.appendChild(make('h3', 'stgb-ss-alternates-heading', labels.answersHeading || ''));
        const grid = make('div', 'stgb-ss-cards');
        cards.forEach((card) => grid.appendChild(card));
        section.appendChild(grid);
        return section;
    };

    const buildFallback = (): HTMLElement => {
        const panel = make('div', 'stgb-ss-fallback');
        panel.appendChild(make('h2', '', labels.noResultHeading || ''));
        panel.appendChild(make('p', '', labels.noResultText || ''));
        if (labels.noResultCtaText) {
            const cta = make('a', 'stgb-ss-fallback-cta', labels.noResultCtaText);
            cta.setAttribute('href', labels.noResultCtaUrl || '#');
            panel.appendChild(cta);
        }
        return panel;
    };

    /* ---- Add all to cart ---- */

    // Adds every id through WooCommerce's own wc-ajax endpoint (the same one
    // wc-add-to-cart.js posts to), sequentially — parallel adds race the Woo
    // session. Fragments are full cart snapshots, so one added_to_cart
    // trigger with the last response refreshes the site's mini cart exactly
    // like a single add does (wc-add-to-cart.js's updateFragments and
    // wc-cart-fragments' sessionStorage sync both listen for it). No $button
    // is passed — that would make Woo append its "View cart" link after our
    // button; the label change below is feedback enough.
    const addAllToCart = async (ids: string[], btn: HTMLButtonElement) => {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        const w = window as any;
        const params = w.wc_add_to_cart_params;
        if (!params || !params.wc_ajax_url) return;
        const url = String(params.wc_ajax_url).replace('%%endpoint%%', 'add_to_cart');

        const original = btn.textContent || '';
        // aria-disabled + the guard above, NOT the disabled property —
        // disabling the focused element would dump keyboard focus to <body>.
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-busy');

        const gen = ++addAllGen;
        const controller = new AbortController();
        addAllAbort = controller;

        // Fires the mini-cart refresh with the freshest snapshot this run got
        // — fragments are full cart snapshots, so this stays correct even for
        // a superseded run (its landed adds are in whatever it last saw).
        const triggerCartRefresh = (data: any) => {
            if (data && data.fragments && w.jQuery) {
                w.jQuery(document.body).trigger('added_to_cart', [data.fragments, data.cart_hash]);
            }
        };

        let last: any = null;
        let failed = false;
        for (const id of ids) {
            try {
                const body = new URLSearchParams();
                body.set('product_id', id);
                body.set('quantity', '1');
                const response = await fetch(url, {
                    method: 'POST',
                    credentials: 'same-origin',
                    body,
                    signal: controller.signal,
                });
                const data = await response.json();
                // Woo signals "couldn't add" (out of stock since render, needs
                // options, …) with error:true instead of an HTTP failure.
                if (data && !data.error) {
                    last = data;
                } else {
                    failed = true;
                }
            } catch {
                failed = true;
            }
            // Superseded (Back / Start Over / a new results screen): stop
            // adding, refresh the mini cart for what already landed, and
            // write nothing to the now-detached button.
            if (gen !== addAllGen) {
                triggerCartRefresh(last);
                return;
            }
        }
        if (addAllAbort === controller) addAllAbort = null;

        triggerCartRefresh(last);

        btn.classList.remove('is-busy');
        btn.textContent = last && !failed
            ? labels.addAllDone || 'Added to Cart'
            : labels.addAllError || 'Couldn’t add everything — use the buttons on each product.';
        window.setTimeout(() => {
            if (gen !== addAllGen) return;
            btn.textContent = original;
            btn.removeAttribute('aria-disabled');
        }, 3000);
    };

    // Every AJAX-addable product currently shown in the results (queried off
    // the rendered cards, so non-AJAX products are naturally excluded and the
    // panel/add-on dedupe carries over). For variable products the button's
    // data-product_id is the currently selected swatch's variation, so this
    // must be read at CLICK time, not results-build time.
    const collectAddAllIds = (): string[] => {
        const seen: Record<string, boolean> = {};
        const ids: string[] = [];
        resultsEl.querySelectorAll<HTMLElement>('.ajax_add_to_cart[data-product_id]').forEach((el) => {
            const id = el.getAttribute('data-product_id');
            if (id && !seen[id]) {
                seen[id] = true;
                ids.push(id);
            }
        });
        return ids;
    };

    // One button that adds everything shown. Null when Woo's params/jQuery
    // are missing or when there's only one product — a lone card's own button
    // already covers that.
    const buildAddAllButton = (): HTMLButtonElement | null => {
        const w = window as any;
        if (!w.wc_add_to_cart_params || !w.wc_add_to_cart_params.wc_ajax_url || !w.jQuery) return null;
        if (collectAddAllIds().length < 2) return null;

        const btn = make('button', 'stgb-ss-add-all', labels.addAllLabel || 'Add All to Cart') as HTMLButtonElement;
        btn.setAttribute('type', 'button');
        btn.addEventListener('click', () => {
            void addAllToCart(collectAddAllIds(), btn);
        });
        return btn;
    };

    /* ---- View transitions ---- */

    const hideAllSteps = () => {
        Object.keys(stepByKey).forEach((key) => {
            stepByKey[key].hidden = true;
        });
    };

    // "Question n of total" where total only counts questions that would still
    // be shown under the current candidate set — it can shrink as answers
    // narrow the rules, which is acceptable per spec.
    const updateProgress = (current: SsQuestion) => {
        if (!progressEl || !showProgress) return;
        const cands = candidatesFor(answers);
        let remaining = 0;
        questions.forEach((q) => {
            if (q.key in answers || q.key === current.key || !stepByKey[q.key]) return;
            if (wouldShow(q, cands)) remaining += 1;
        });
        const n = trail.length + 1;
        progressEl.textContent = 'Question ' + n + ' of ' + (n + remaining);
    };

    // moveFocus is false only on the initial paint — grabbing focus on page
    // load would yank the visitor's scroll position to the quiz.
    const showStep = (q: SsQuestion, moveFocus: boolean) => {
        const step = stepByKey[q.key];
        const visible = visibleOptions(q, candidatesFor(answers));
        const visibleValues: Record<string, boolean> = {};
        visible.forEach((o) => {
            visibleValues[o.value] = true;
        });

        step.querySelectorAll<HTMLElement>('.stgb-ss-option').forEach((optionEl) => {
            const isVisible = !!visibleValues[optionEl.dataset.value || ''];
            optionEl.hidden = !isVisible;
            if (!isVisible) {
                // A stale checked radio on a now-impossible option would
                // otherwise resurface via Back.
                const input = optionEl.querySelector<HTMLInputElement>('.stgb-ss-option-input');
                if (input && input.checked) input.checked = false;
            }
        });

        hideAllSteps();
        step.hidden = false;
        resultsEl.hidden = true;
        if (backBtn) backBtn.hidden = trail.length === 0;
        updateProgress(q);

        if (moveFocus) {
            step.tabIndex = -1;
            step.focus();
        }
    };

    const showResults = (moveFocus: boolean) => {
        // A rebuilt results screen gets a fresh add-all button; any run still
        // in flight belongs to the torn-down one.
        invalidateAddAll();
        hideAllSteps();
        // Back stays available from results (§5: hidden only when the trail is
        // empty) so the visitor can revise just their last answer.
        if (backBtn) backBtn.hidden = trail.length === 0;
        if (progressEl && showProgress) progressEl.textContent = '';
        resultsEl.textContent = '';

        const cands = candidatesFor(answers);

        // Grouped mode: a "group by" question was answered with a wildcard, so
        // show one panel per concrete option the visitor could have picked.
        const groupQ = questions.find(
            (q) => !!q.groupWhenWildcard && q.key in answers && isWildcardValue(q.key, answers[q.key])
        );

        let heading: HTMLElement | null = null;
        let hasContent = false;
        // Every product id rendered on this results screen, across panels and
        // add-ons — the answer add-ons dedupe against it.
        const shownIds = new Set<number>();

        if (groupQ) {
            const sections: HTMLElement[] = [];
            visibleOptions(groupQ, cands).forEach((option) => {
                if (option.isWildcard) return;
                const hypothetical: Record<string, string> = {};
                Object.keys(answers).forEach((key) => {
                    hypothetical[key] = answers[key];
                });
                hypothetical[groupQ.key] = option.value;
                const groupCands = candidatesFor(hypothetical);
                if (!groupCands.length) return;
                const rule = bestRule(groupCands, hypothetical);
                const panel = rule ? buildPanel(rule, shownIds) : null;
                if (!panel) return;

                const section = make('section', 'stgb-ss-group');
                section.appendChild(make('h3', 'stgb-ss-group-heading', option.label));
                if (option.description) {
                    section.appendChild(make('p', 'stgb-ss-group-desc', option.description));
                }
                section.appendChild(panel);
                sections.push(section);
            });
            if (sections.length) {
                heading = make('h2', 'stgb-ss-results-heading', labels.resultsHeading || '');
                resultsEl.appendChild(heading);
                sections.forEach((section) => resultsEl.appendChild(section));
                hasContent = true;
            }
        } else {
            const rule = cands.length ? bestRule(cands, answers) : null;
            const panel = rule ? buildPanel(rule, shownIds) : null;
            if (panel) {
                heading = make('h2', 'stgb-ss-results-heading', labels.resultsHeading || '');
                resultsEl.appendChild(heading);
                resultsEl.appendChild(panel);
                hasContent = true;
            }
        }

        // Products the answers themselves imply (linked-option products). Not
        // on the fallback screen — "we need more info" plus a lone mount card
        // would read as the recommendation.
        if (hasContent) {
            const addons = buildAnswerAddons(shownIds);
            if (addons) resultsEl.appendChild(addons);
        }

        // Zero candidates, unrenderable best rule, or zero groups.
        if (!hasContent) {
            const fallback = buildFallback();
            heading = fallback.querySelector('h2');
            resultsEl.appendChild(fallback);
        }

        const footer = make('div', 'stgb-ss-results-footer');
        // No add-all across grouped results: the groups are alternatives (one
        // per mount they could pick), not a set to buy together.
        if (hasContent && !groupQ) {
            const addAllBtn = buildAddAllButton();
            if (addAllBtn) footer.appendChild(addAllBtn);
        }
        const restartBtn = make('button', 'stgb-ss-restart', labels.restartLabel || 'Start Over');
        restartBtn.setAttribute('type', 'button');
        restartBtn.addEventListener('click', restart);
        footer.appendChild(restartBtn);
        resultsEl.appendChild(footer);

        resultsEl.hidden = false;

        // Freshly cloned cards carry the template's authored CTA text — let
        // the shared card script stamp their "N in cart" labels.
        document.dispatchEvent(new CustomEvent('stgb:cards-updated'));

        if (moveFocus && heading) {
            heading.tabIndex = -1;
            heading.focus();
        }
    };

    // Shows the next unanswered question, or the results when none remain.
    const start = (moveFocus: boolean) => {
        const q = nextQuestion();
        if (q) {
            showStep(q, moveFocus);
        } else {
            showResults(moveFocus);
        }
    };

    /* ---- Actions ---- */

    const cancelAdvance = () => {
        if (advanceTimer !== undefined) {
            window.clearTimeout(advanceTimer);
            advanceTimer = undefined;
        }
    };

    const selectAnswer = (q: SsQuestion, value: string) => {
        answers[q.key] = value;
        if (trail.indexOf(q.key) === -1) trail.push(q.key);
        // Brief pause so the chosen radio is visibly selected before the step
        // changes; picking again while pending just reschedules.
        cancelAdvance();
        advanceTimer = window.setTimeout(() => {
            advanceTimer = undefined;
            start(true);
        }, 150);
    };

    const restart = () => {
        cancelAdvance();
        invalidateAddAll();
        Object.keys(answers).forEach((key) => {
            delete answers[key];
        });
        trail.length = 0;
        form.reset();
        resultsEl.hidden = true;
        resultsEl.textContent = '';
        // Back to the start screen (the pre-question state), not straight
        // into question one — falls through for legacy markup without one.
        if (headerEl && startBtn) {
            hideAllSteps();
            if (backBtn) backBtn.hidden = true;
            if (progressEl) progressEl.textContent = '';
            headerEl.hidden = false;
            startBtn.focus();
        } else {
            start(true);
        }
    };

    const goBack = () => {
        cancelAdvance();
        invalidateAddAll();
        const key = trail.pop();
        if (!key) return;
        delete answers[key];
        const q = questionByKey[key];
        // The radio stays checked as-was so the visitor sees — and can re-pick
        // — their previous choice.
        if (q) showStep(q, true);
    };

    /* ---- Wiring ---- */

    const handleSelect = (event: Event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.classList.contains('stgb-ss-option-input') || !target.checked) return;
        const step = target.closest('.stgb-ss-step') as HTMLElement | null;
        const key = step ? step.dataset.question : undefined;
        const q = key ? questionByKey[key] : undefined;
        if (!q || !step || step.hidden) return;
        // Bound to change AND click: after Back the prior radio is still
        // checked, so re-picking it fires no change event. The value guard
        // dedupes the click+change pair a fresh selection produces.
        if (answers[q.key] === target.value) return;
        selectAnswer(q, target.value);
    };
    form.addEventListener('change', handleSelect);
    form.addEventListener('click', handleSelect);

    // The form has no submit control, but implicit submission would reload the
    // page and wipe the quiz state.
    form.addEventListener('submit', (event) => event.preventDefault());

    backBtn?.addEventListener('click', goBack);

    // Load with only the start screen showing; the quiz begins on the CTA.
    // Legacy markup without a start screen boots straight into question one.
    if (headerEl && startBtn) {
        startBtn.addEventListener('click', () => {
            headerEl.hidden = true;
            start(true);
        });
    } else {
        start(false);
    }
};

const initAll = () => {
    // The front-end script is also enqueued in the editor; skip it there so it
    // never fights with the React-rendered builder UI.
    if (document.body.classList.contains('wp-admin')) return;
    document.querySelectorAll<HTMLElement>('.stgb-suppressor-selector').forEach(initSelector);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
} else {
    initAll();
}

// Mark this side-effect file as a module so its top-level constants are
// module-scoped (avoids a global-scope name clash with other blocks' react.ts).
export {};
